import { useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_LINKEDIN_CAROUSEL_CARD_HEADLINE_MAX_LENGTH,
  ADS_CAMPAIGN_LINKEDIN_CAROUSEL_CARD_HEADLINE_LEADS_MAX_LENGTH,
  ADS_CAMPAIGN_LINKEDIN_CAROUSEL_MIN_CARDS,
  ADS_CAMPAIGN_LINKEDIN_CAROUSEL_MAX_CARDS,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  ADS_CAMPAIGN_STATUS_DRAFT,
  IMAGE_FORMATS,
  LINKEDIN,
  MIN_DIMENSIONS_CONTENT,
} from "../../../../utils/constants";
import { useCreditActionCosts } from "../../../../hooks/useCreditActionCosts";
import {
  TITLE_PLACEHOLDER,
  HEADLINE_PLACEHOLDER,
  DESTINATION_URL_PLACEHOLDER,
  CONTENT,
  CARD,
  FIELD_REQUIRED,
  FIELD_URL_VALID,
  INSTANT_FORM_PLACEHOLDER,
  UTM_PARAMETERS_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import TextFieldWithActions from "../../../Form/TextFieldWithActions";
import ContentField from "../../../Form/ContentField";
import CardContainer from "../../../Card/CardContainer";
import { ChipAIGeneratedSection } from "../../../Chip/ChipAIGenerated";
import ChipOptional from "../../../Chip/ChipOptional";
import { REGEX_URL } from "../../../../utils/regex";
import { useDispatchApp } from "../../../../lib/redux";
import { generateAdsCampaignDescription } from "../../../../actions/adsCampaigns";
import { getIsBlockedActions } from "../../../../actions/getters";
import { generalFormatMediaUrl } from "../../../../services/adsCampaigns";
import { getUniqueID } from "../../../../utils/numbers";
import { BOX_SHADOW_THEME, INPUT_COLOR_BORDER } from "../../../../utils/colors";
import LeadFormContainer from "../../../LinkedIn/LeadFormContainer";
import ClickableText from "../../../Texts/ClickableText";
import { SimpleAlert } from "../../../Alerts/Alerts";
import {
  COPY_VALUES_IN_OTHER_ADGROUPS,
  READY,
} from "../../../../i18n/keysTranslations";
import { ALERT_ICON_TYPE_SUCCESS } from "../../../../utils/constants";
import UtmParametersContainer from "../../../Common/Utms/UtmParametersContainer";
import useApprovalFieldNavigateTab from "../../../../pages/NewAdsCampaign/hooks/useApprovalFieldNavigateTab";
import { alertIfEmptyCopyValue } from "../../../../utils/copyFormValue";
import {
  commitGeneratedSectionText,
  copySectionValue,
  readStoredSectionFingerprint,
} from "../../../../utils/aiGeneratedForm";

const AssetsGroupLinkedinCarouselForm = ({
  BASIC_PATH,
  control,
  errors,
  trigger,
  setValue,
  getValues,
  watch,
  isDisabledFields,
  platform,
  objective,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_TEXT_ACTION } = useCreditActionCosts();
  const platformData = watch(`platformsData.${platform}`) || {};
  const adFormat = platformData?.adFormat;
  const isLeadsObjective = objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION;
  const [tabIndex, setTabIndex] = useState(0);

  useApprovalFieldNavigateTab({
    formPath: BASIC_PATH,
    onSelectIndex: setTabIndex,
  });

  const language = watch("language");
  const mediaUrls = watch(`${BASIC_PATH}.mediaUrls`) || [];
  const headlines = watch(`${BASIC_PATH}.headlines`) || [];
  const destinationUrls = watch(`${BASIC_PATH}.destinationUrls`) || [];
  const assetGroupStatus = watch(`${BASIC_PATH}.status`);

  // Disable leadForm if objective is LEADS and status is not DRAFT
  const isLeadsFieldsDisabled =
    isLeadsObjective && assetGroupStatus !== ADS_CAMPAIGN_STATUS_DRAFT;

  const maxLengthPrimaryText = ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[LINKEDIN];

  // Headline max length depends on objective
  const maxLengthHeadline =
    objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION
      ? ADS_CAMPAIGN_LINKEDIN_CAROUSEL_CARD_HEADLINE_LEADS_MAX_LENGTH
      : ADS_CAMPAIGN_LINKEDIN_CAROUSEL_CARD_HEADLINE_MAX_LENGTH;

  const minCards = ADS_CAMPAIGN_LINKEDIN_CAROUSEL_MIN_CARDS;
  const maxCards = ADS_CAMPAIGN_LINKEDIN_CAROUSEL_MAX_CARDS;

  const showDeleteCardIcon = mediaUrls.length > minCards;
  const showAddCardIcon = mediaUrls.length < maxCards;

  const onChangeTab = (event, newValue) => {
    setTabIndex(newValue);
  };

  const onGenerateText = async ({ type, index = 0 }) => {
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
        showUsageConfirmationAlert: true,
      })
    );
    if (isBlocked) return;

    const currentTexts = getValues(`${BASIC_PATH}.${type}`) || [];
    const currentText = currentTexts[index]?.value || "";
    const allTexts = currentTexts.map((item) => item.value || "");
    const textsWithCurrentFirst = [
      currentText,
      ...allTexts.filter((_, i) => i !== index),
    ];
    const destinationUrls = getValues(`${BASIC_PATH}.destinationUrls`) || [];
    const destinationUrl = destinationUrls[index]?.value || "";

    const objectiveValue = getValues(`platformsData.${platform}.objective`);
    const response = await dispatch(
      generateAdsCampaignDescription({
        campaignID: getValues("id"),
        objective: objectiveValue,
        adFormat,
        platform,
        textIndex: index,
        campaignContext: getValues("context") || "",
        language: getValues("language"),
        destinationUrl,
        type,
        currentTexts: textsWithCurrentFirst,
        entityType: "assetsGroup",
        currentFingerprint: readStoredSectionFingerprint({
          getValues,
          basePath: BASIC_PATH,
          section: type,
        }),
      })
    );

    commitGeneratedSectionText({
      getValues,
      setValue,
      trigger,
      basePath: BASIC_PATH,
      section: type,
      index,
      platform,
      entityType: "assetsGroup",
      response,
    });
  };

  const onAddCard = () => {
    const currentMediaUrls = getValues(`${BASIC_PATH}.mediaUrls`) || [];
    const currentHeadlines = getValues(`${BASIC_PATH}.headlines`) || [];
    const currentDestinationUrls =
      getValues(`${BASIC_PATH}.destinationUrls`) || [];

    setValue(`${BASIC_PATH}.mediaUrls`, [...currentMediaUrls, ""], {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`${BASIC_PATH}.headlines`, [
      ...currentHeadlines,
      { id: getUniqueID(), value: "" },
    ]);
    setValue(`${BASIC_PATH}.destinationUrls`, [
      ...currentDestinationUrls,
      { id: getUniqueID(), value: "" },
    ]);

    setTabIndex(currentMediaUrls.length);
  };

  const onRemoveCard = (index) => {
    setValue(
      `${BASIC_PATH}.mediaUrls`,
      mediaUrls.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    setValue(
      `${BASIC_PATH}.headlines`,
      headlines.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    setValue(
      `${BASIC_PATH}.destinationUrls`,
      destinationUrls.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    if (index === tabIndex && index > 0) {
      setTabIndex(index - 1);
    } else if (index < tabIndex) {
      setTabIndex(tabIndex - 1);
    }
    trigger(`${BASIC_PATH}.mediaUrls`);
    trigger(`${BASIC_PATH}.headlines`);
    trigger(`${BASIC_PATH}.destinationUrls`);
  };

  const onCopyValuesInOtherAds = ({ type }) => {
    const value = getValues(`${BASIC_PATH}.${type}`);
    if (alertIfEmptyCopyValue({ value, t })) return;
    const assetsGroups = getValues(`assetsGroups`);

    assetsGroups.forEach((assetsGroup, currentIndexAssetsGroup) => {
      if (assetsGroup.platform !== platform) return;

      copySectionValue({
        setValue,
        getValues,
        sourceBasePath: BASIC_PATH,
        section: type,
        platform,
        entityType: "assetsGroup",
        path: `assetsGroups.[${currentIndexAssetsGroup}].${type}`,
        value,
      });
    });

    SimpleAlert({
      title: t(READY),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
  };

  return (
    <Grid container spacing={3}>
      {/* Lead Form */}
      {isLeadsObjective && (
        <Grid item xs={12}>
          <CardContainer
            aiGeneratedSection={{
              basePath: BASIC_PATH,
              section: "leadForm",
            }}
            title={
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(INSTANT_FORM_PLACEHOLDER)}
              </Typography>
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <LeadFormContainer
                name={`${BASIC_PATH}.leadForm`}
                integrationID={platformData?.integrationID}
                adAccountID={platformData?.adAccountID}
                control={control}
                errors={errors}
                disabled={isDisabledFields || isLeadsFieldsDisabled}
              />
              <ClickableText
                disabled={isDisabledFields || isLeadsFieldsDisabled}
                onClick={() => onCopyValuesInOtherAds({ type: "leadForm" })}
              >
                {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
              </ClickableText>
            </Box>
          </CardContainer>
        </Grid>
      )}
      {/* Primary Text (shared across all cards) */}
      <Grid item xs={12}>
        <CardContainer
          aiGeneratedSection={{
            basePath: BASIC_PATH,
            section: "titles",
          }}
          title={
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(TITLE_PLACEHOLDER)}
              </Typography>
              <ClickableText
                disabled={isDisabledFields}
                onClick={() => onCopyValuesInOtherAds({ type: "titles" })}
              >
                {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
              </ClickableText>
            </Box>
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <TextFieldWithActions
              type="titles"
              labelTitle=""
              name={`${BASIC_PATH}.titles.[0].value`}
              disabled={
                isDisabledFields ||
                assetGroupStatus !== ADS_CAMPAIGN_STATUS_DRAFT
              }
              platform={platform}
              showAddIcon={false}
              showDeleteIcon={false}
              maxLength={maxLengthPrimaryText}
              showCounterCharacters={true}
              onGenerate={async () => {
                await onGenerateText({ type: "titles", index: 0 });
              }}
              control={control}
              errors={errors}
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
              }}
            />
          </Box>
        </CardContainer>
      </Grid>

      {/* UTM Parameters */}
      <Grid item xs={12}>
        <CardContainer
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(UTM_PARAMETERS_PLACEHOLDER)}
              </Typography>
              <ChipOptional size="small" />
            </Box>
          }
        >
          <UtmParametersContainer
            name={`${BASIC_PATH}.utmParameters`}
            control={control}
            errors={errors}
            disabled={isDisabledFields}
            platform={platform}
            onCopyValuesInOtherAds={() =>
              onCopyValuesInOtherAds({ type: "utmParameters" })
            }
            copyText={COPY_VALUES_IN_OTHER_ADGROUPS}
          />
        </CardContainer>
      </Grid>

      {/* Carousel Cards */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <ChipAIGeneratedSection basePath={BASIC_PATH} section="headlines" />
        </Box>
        <Tabs
          value={tabIndex}
          onChange={onChangeTab}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Carousel cards tabs"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {mediaUrls.map((_, index) => (
            <Tab key={index} label={`${t(CARD)} ${index + 1}`} />
          ))}
        </Tabs>

        {/* Card Content */}
        <Box sx={{ mt: 1 }}>
          {mediaUrls.map((_, index) => {
            const headline = headlines[index] || { value: "" };
            const destinationUrl = destinationUrls[index] || { value: "" };

            const tabKey = destinationUrl?.id || `${index}`;

            return (
              <Box
                key={tabKey}
                sx={{
                  display: index === tabIndex ? "block" : "none",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    background: "fields.background",
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: BOX_SHADOW_THEME,
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  {/* Card Actions */}
                  {!isDisabledFields &&
                    assetGroupStatus === ADS_CAMPAIGN_STATUS_DRAFT && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        {showAddCardIcon && (
                          <IconButton
                            type="button"
                            onClick={(e) => {
                              e?.stopPropagation();
                              onAddCard();
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        )}
                        {showDeleteCardIcon && (
                          <IconButton
                            onClick={(e) => {
                              e?.stopPropagation();
                              onRemoveCard(index);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    )}

                  <Grid container spacing={3} sx={{ px: 2 }}>
                    {/* Card Fields */}
                    <Grid item xs={12} md={8}>
                      <Grid container spacing={2}>
                        {/* Destination URL */}
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body1">
                              {t(DESTINATION_URL_PLACEHOLDER)}
                            </Typography>
                            <ClickableText
                              disabled={isDisabledFields}
                              onClick={() =>
                                onCopyValuesInOtherAds({
                                  type: "destinationUrls",
                                })
                              }
                            >
                              {t(COPY_VALUES_IN_OTHER_ADGROUPS)}
                            </ClickableText>
                          </Box>
                          <TextFieldWithActions
                            key={destinationUrl.id}
                            type="destinationUrl"
                            labelTitle=""
                            name={`${BASIC_PATH}.destinationUrls.[${index}].value`}
                            disabled={
                              isDisabledFields ||
                              assetGroupStatus !== ADS_CAMPAIGN_STATUS_DRAFT
                            }
                            platform={platform}
                            showAddIcon={false}
                            showDeleteIcon={false}
                            showCounterCharacters={false}
                            control={control}
                            errors={errors}
                            rules={{
                              required: {
                                value: true,
                                message: t(FIELD_REQUIRED),
                              },
                              validate: (value) => {
                                if (!REGEX_URL.test(encodeURI(value))) {
                                  return t(FIELD_URL_VALID);
                                }
                              },
                            }}
                          />
                        </Grid>

                        {/* Card Headline */}
                        <Grid item xs={12}>
                          <TextFieldWithActions
                            key={headline.id}
                            type="headlines"
                            labelTitle={t(HEADLINE_PLACEHOLDER)}
                            name={`${BASIC_PATH}.headlines.[${index}].value`}
                            disabled={
                              isDisabledFields ||
                              assetGroupStatus !== ADS_CAMPAIGN_STATUS_DRAFT
                            }
                            platform={platform}
                            showAddIcon={false}
                            showDeleteIcon={false}
                            maxLength={maxLengthHeadline}
                            showCounterCharacters={true}
                            onGenerate={async () => {
                              await onGenerateText({
                                type: "headlines",
                                index,
                              });
                            }}
                            control={control}
                            errors={errors}
                            rules={{
                              required: {
                                value: true,
                                message: t(FIELD_REQUIRED),
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Card Image */}
                    <Grid item xs={12} md={4}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {t(CONTENT)}
                      </Typography>
                      <ContentField
                        platform={LINKEDIN}
                        previewMode="carouselCard"
                        propsSelectContentComponent={{
                          language,
                          acceptedFormats: IMAGE_FORMATS,
                          showButtonUpload: true,
                          showGenerateWithAIOption: false,
                          showUploadDeviceOption: true,
                          showGalleryOption: true,
                        }}
                        minDimensions={MIN_DIMENSIONS_CONTENT}
                        onCallbackSave={({ from, content }) => {
                          const contentFormatted = generalFormatMediaUrl({
                            content,
                            from,
                          });
                          setValue(
                            `${BASIC_PATH}.mediaUrls.[${index}]`,
                            contentFormatted.url,
                            {
                              shouldValidate: true,
                              shouldDirty: true,
                            }
                          );
                        }}
                        label=""
                        name={`${BASIC_PATH}.mediaUrls.[${index}]`}
                        control={control}
                        errors={errors}
                        disabled={
                          isDisabledFields ||
                          assetGroupStatus !== ADS_CAMPAIGN_STATUS_DRAFT
                        }
                        rules={{
                          required: {
                            value: true,
                            message: t(FIELD_REQUIRED),
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Grid>
    </Grid>
  );
};

export default AssetsGroupLinkedinCarouselForm;
